import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Toggle } from '../ui/BaseComponents';
import { Logger } from '../../utils/logger';
import { llmStorage } from '../../services/storageService';
import { notifyWarning } from '../../services/notificationService';
import { 
  getCategoriesWithTemplatesIncludingCustom,
  getTemplateByIdWithCustom,
  getTemplateSystemPrompt,
  getTemplateExampleTags,
  saveCustomTemplate,
  deleteCustomTemplate,
  getCustomTemplates,
  TEMPLATE_CATEGORIES
} from '../../utils/llm-generator/promptTemplates';
import {
  getCategoriesWithTemplatesForUser,
  getTemplateForUser,
  saveUserTemplate,
  deleteUserTemplate,
  forkSystemTemplate,
  subscribeToUserTemplates
} from '../../utils/llm-generator/templateService';

const log = Logger.create('LLMSettingsTab2');
import { hasNativeFileSupport, getOpenRouterModelInfo } from '../../utils/llm-generator/apiClients';

// Storage key prefix for provider-specific API keys
const API_KEY_STORAGE_PREFIX = 'llm_api_key_';

// Simple encryption (not military-grade, but better than plaintext)
const encryptApiKey = (key, salt) => {
  if (!key) return '';
  const encoded = btoa(key);
  return encoded.split('').map((char, i) => 
    String.fromCharCode(char.charCodeAt(0) ^ salt.charCodeAt(i % salt.length))
  ).join('');
};

const decryptApiKey = (encrypted, salt) => {
  if (!encrypted) return '';
  try {
    const decoded = encrypted.split('').map((char, i) =>
      String.fromCharCode(char.charCodeAt(0) ^ salt.charCodeAt(i % salt.length))
    ).join('');
    return atob(decoded);
  } catch {
    return '';
  }
};

// Generate or retrieve device-specific encryption salt
const getEncryptionSalt = () => {
  let salt = llmStorage.get('encryption_salt');
  
  if (!salt) {
    // Generate cryptographically random salt
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    salt = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    llmStorage.set('encryption_salt', salt);
  }
  
  return salt;
};

/**
 * LLMSettingsTab2 - Tab for LLM provider configuration
 * Supports provider-specific API keys, templates, and EXPLICIT save (no auto-save)
 */
function LLMSettingsTab2({
  // Provider Settings
  llmProvider,
  setLlmProvider,
  apiKey,
  setApiKey,
  baseUrl,
  setBaseUrl,
  model,
  setModel,
  temperature,
  setTemperature,
  maxTokens,
  setMaxTokens,
  // Structured Outputs
  useStructuredOutputs,
  setUseStructuredOutputs,
  // JSON Repair
  enableJsonRepair,
  setEnableJsonRepair,
  // Conversation History
  useConversationHistory,
  setUseConversationHistory,
  // Template System
  selectedTemplateId,
  setSelectedTemplateId,
  // System Prompt
  systemPrompt,
  setSystemPrompt,
  getDefaultSystemPrompt,
  // Actions
  onSaveSettings,
  onResetSettings,
  // Callback for unsaved changes (optional)
  onUnsavedChangesChange,
  // User Auth (for Firebase templates)
  userId
}) {
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [saveApiKey, setSaveApiKey] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('mint');
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialApiKey, setInitialApiKey] = useState('');
  
  // Notify parent about unsaved changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChangesChange]);
  
  // Firebase templates state
  const [firebaseCategories, setFirebaseCategories] = useState([]);
  const [useFirebase, setUseFirebase] = useState(!!userId);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Custom template editing state
  const [showCustomTemplateEditor, setShowCustomTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [customTemplateForm, setCustomTemplateForm] = useState({
    name: '',
    icon: '📝',
    description: '',
    systemPrompt: '',
    exampleTags: ''
  });
  
  // Track which providers have saved keys
  const [savedProviders, setSavedProviders] = useState({});
  
  // Refresh trigger for custom templates
  const [customTemplatesRefresh, setCustomTemplatesRefresh] = useState(0);
  
  // OpenRouter PDF support check state
  const [checkingPdfSupport, setCheckingPdfSupport] = useState(false);
  const [pdfSupportResult, setPdfSupportResult] = useState(null); // null | { hasSupport: boolean, modelInfo: object | null }
  
  // Get categories with templates - use Firebase if userId available, otherwise localStorage
  const localCategories = getCategoriesWithTemplatesIncludingCustom();
  const categoriesWithTemplates = useFirebase && firebaseCategories.length > 0 
    ? firebaseCategories 
    : localCategories;
    
  // Load Firebase templates when userId changes
  useEffect(() => {
    if (userId && useFirebase) {
      setLoadingTemplates(true);
      getCategoriesWithTemplatesForUser(userId)
        .then(categories => {
          setFirebaseCategories(categories);
          setLoadingTemplates(false);
        })
        .catch(err => {
          log.error('Error loading Firebase templates', err);
          setUseFirebase(false);
          setLoadingTemplates(false);
        });
        
      // Subscribe to realtime updates
      const unsubscribe = subscribeToUserTemplates(userId, (userTemplates) => {
        // Refresh categories when user templates change
        getCategoriesWithTemplatesForUser(userId).then(setFirebaseCategories);
      });
      
      return () => unsubscribe();
    }
  }, [userId, useFirebase, customTemplatesRefresh]);

  // Get storage key for current provider
  const getStorageKey = useCallback((provider) => {
    return `${API_KEY_STORAGE_PREFIX}${provider}`;
  }, []);

  // Load saved API keys indicator on mount (but DON'T load into field)
  useEffect(() => {
    const salt = getEncryptionSalt();
    const providers = ['openai', 'anthropic', 'groq', 'openrouter', 'custom'];
    const saved = {};
    
    providers.forEach(provider => {
      const encrypted = llmStorage.get(`api_key_${provider}`);
      if (encrypted) {
        const decrypted = decryptApiKey(encrypted, salt);
        if (decrypted) {
          saved[provider] = true;
        }
      }
    });
    
    setSavedProviders(saved);
    
    // Check if current provider has saved key (for checkbox state)
    if (saved[llmProvider]) {
      setSaveApiKey(true);
    }
    
    // Store initial API key to track changes
    setInitialApiKey(apiKey || '');
  }, []);

  // When provider changes, update checkbox state but DON'T auto-fill API key
  useEffect(() => {
    // Update checkbox based on whether this provider has a saved key
    setSaveApiKey(!!savedProviders[llmProvider]);
    // Clear the API key field when switching providers
    setApiKey('');
    setHasUnsavedChanges(false);
  }, [llmProvider]);

  // Handle API key save toggle (just toggles the checkbox, doesn't save yet)
  const handleSaveApiKeyToggle = (checked) => {
    setSaveApiKey(checked);
    setHasUnsavedChanges(true);
  };

  // Track changes to API key
  const handleApiKeyChange = (value) => {
    setApiKey(value);
    setHasUnsavedChanges(true);
  };
  
  // Load saved API key into field (explicit action)
  const handleLoadSavedKey = () => {
    const salt = getEncryptionSalt();
    const encrypted = llmStorage.get(`api_key_${llmProvider}`);
    if (encrypted) {
      const decrypted = decryptApiKey(encrypted, salt);
      if (decrypted) {
        setApiKey(decrypted);
        setSaveApiKey(true);
      }
    }
  };
  
  // Explicit save function for API key
  const handleSaveApiKey = () => {
    const salt = getEncryptionSalt();
    
    if (saveApiKey && apiKey) {
      const encrypted = encryptApiKey(apiKey, salt);
      llmStorage.set(`api_key_${llmProvider}`, encrypted);
      setSavedProviders(prev => ({ ...prev, [llmProvider]: true }));
    } else if (!saveApiKey) {
      llmStorage.remove(`api_key_${llmProvider}`);
      setSavedProviders(prev => ({ ...prev, [llmProvider]: false }));
    }
  };

  // Handle provider change - load saved API key for that provider
  const handleProviderChange = (newProvider) => {
    setLlmProvider(newProvider);
    
    // Only set base URL for providers that need it
    if (newProvider === 'groq') {
      setBaseUrl('https://api.groq.com/openai/v1/chat/completions');
    } else if (newProvider === 'openrouter') {
      setBaseUrl('https://openrouter.ai/api/v1/chat/completions');
    } else if (newProvider === 'clipboard') {
      setBaseUrl('');
      setApiKey('');
      setModel('N/A');
    } else if (newProvider === 'openai' || newProvider === 'anthropic') {
      setBaseUrl('');
    }
    // API key will be loaded by useEffect
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    if (setSelectedTemplateId) {
      setSelectedTemplateId(templateId);
      // Load template's system prompt (works for both built-in and custom)
      const template = getTemplateByIdWithCustom(templateId);
      if (template?.systemPrompt) {
        setSystemPrompt(template.systemPrompt);
      } else {
        const templatePrompt = getTemplateSystemPrompt(templateId);
        if (templatePrompt) {
          setSystemPrompt(templatePrompt);
        }
      }
    }
  };

  // Get current template's example tags
  const currentExampleTags = selectedTemplateId ? getTemplateExampleTags(selectedTemplateId) : [];
  const currentTemplate = selectedTemplateId ? getTemplateByIdWithCustom(selectedTemplateId) : null;

  // Handle save ALL settings with feedback
  const handleSaveAll = () => {
    // Save API key if checkbox is checked
    handleSaveApiKey();
    // Save other settings
    onSaveSettings();
    // Reset unsaved state
    setHasUnsavedChanges(false);
    // Show confirmation
    setShowSaveConfirm(true);
    setTimeout(() => setShowSaveConfirm(false), 2000);
  };
  
  // Save Button Component (reusable)
  const SaveButton = ({ className = '' }) => (
    <button
      onClick={handleSaveAll}
      className={`py-2.5 px-6 rounded-lg font-medium transition-all flex items-center gap-2 ${
        hasUnsavedChanges
          ? 'bg-success-600 hover:bg-success-700 text-white shadow-lg animate-pulse'
          : 'bg-success-600 hover:bg-success-700 text-white'
      } ${className}`}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      {hasUnsavedChanges ? 'Änderungen speichern' : 'Gespeichert'}
    </button>
  );
  
  // Custom Template Functions
  const handleCreateCustomTemplate = () => {
    setEditingTemplate(null);
    setCustomTemplateForm({
      name: '',
      icon: '📝',
      description: '',
      systemPrompt: systemPrompt || '', // Pre-fill with current system prompt
      exampleTags: ''
    });
    setShowCustomTemplateEditor(true);
  };
  
  const handleEditCustomTemplate = (template) => {
    setEditingTemplate(template);
    setCustomTemplateForm({
      name: template.name || '',
      icon: template.icon || '📝',
      description: template.description || '',
      systemPrompt: template.systemPrompt || '',
      exampleTags: (template.exampleTags || []).join(', ')
    });
    setShowCustomTemplateEditor(true);
  };
  
  const handleSaveCustomTemplate = async () => {
    const tags = customTemplateForm.exampleTags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    const templateData = {
      id: editingTemplate?.id || undefined, // Will auto-generate if new
      name: customTemplateForm.name || 'Eigene Vorlage',
      icon: customTemplateForm.icon || '📝',
      description: customTemplateForm.description || '',
      systemPrompt: customTemplateForm.systemPrompt || '',
      exampleTags: tags,
      createdAt: editingTemplate?.createdAt
    };
    
    // Use Firebase if userId available, otherwise localStorage
    let result;
    if (userId && useFirebase) {
      result = await saveUserTemplate(userId, templateData);
    } else {
      result = saveCustomTemplate(templateData);
    }
    
    if (result.success) {
      setShowCustomTemplateEditor(false);
      setCustomTemplatesRefresh(prev => prev + 1); // Trigger refresh
      // Select the newly created/edited template
      if (result.id) {
        handleTemplateSelect(result.id);
      }
    }
  };
  
  const handleDeleteCustomTemplate = async (templateId) => {
    if (window.confirm('Möchtest du diese Vorlage wirklich löschen?')) {
      // Use Firebase if userId available, otherwise localStorage
      if (userId && useFirebase) {
        await deleteUserTemplate(userId, templateId);
      } else {
        deleteCustomTemplate(templateId);
      }
      setCustomTemplatesRefresh(prev => prev + 1);
      // If deleted template was selected, switch to coding
      if (selectedTemplateId === templateId) {
        handleTemplateSelect('coding');
      }
    }
  };
  
  // Fork system template to user's collection
  const handleForkTemplate = async (templateId) => {
    if (!userId) {
      notifyWarning('Bitte melde dich an, um Templates zu forken.');
      return;
    }
    
    const result = await forkSystemTemplate(userId, templateId);
    if (result.success) {
      setCustomTemplatesRefresh(prev => prev + 1);
      handleTemplateSelect(result.id);
    }
  };
  
  // Check PDF support for OpenRouter model
  const handleCheckPdfSupport = async () => {
    if (!model) {
      setPdfSupportResult({ hasSupport: false, modelInfo: null, error: 'Bitte gib zuerst ein Modell ein' });
      return;
    }
    
    setCheckingPdfSupport(true);
    setPdfSupportResult(null);
    
    try {
      const hasSupport = await hasNativeFileSupport(model);
      const modelInfo = await getOpenRouterModelInfo(model);
      
      setPdfSupportResult({
        hasSupport,
        modelInfo,
        inputModalities: modelInfo?.architecture?.input_modalities || [],
        error: modelInfo ? null : 'Modell nicht in OpenRouter gefunden (Fallback-Erkennung verwendet)'
      });
    } catch (error) {
      setPdfSupportResult({
        hasSupport: false,
        modelInfo: null,
        error: error.message
      });
    } finally {
      setCheckingPdfSupport(false);
    }
  };
  
  // Reset PDF check when model changes
  useEffect(() => {
    setPdfSupportResult(null);
  }, [model]);

  // Provider options with colors for visual distinction
  const providers = [
    { id: 'openai', label: 'OpenAI', description: 'GPT-4, GPT-3.5' },
    { id: 'anthropic', label: 'Anthropic', description: 'Claude 3.5' },
    { id: 'groq', label: 'Groq', description: 'Llama, Mixtral' },
    { id: 'openrouter', label: 'OpenRouter', description: 'Viele Modelle' },
    { id: 'clipboard', label: 'Clipboard', description: 'Prompt kopieren' },
    { id: 'custom', label: 'Custom', description: 'Eigener Endpoint' }
  ];
  
  // Get current provider info
  const currentProvider = providers.find(p => p.id === llmProvider) || providers[0];

  return (
    <div className="space-y-6">
      {/* Save Confirmation Banner */}
      {showSaveConfirm && (
        <div className="fixed top-4 right-4 z-50 bg-success-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Einstellungen gespeichert
        </div>
      )}

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-yellow-600 dark:text-yellow-400">Du hast ungespeicherte Änderungen</span>
          </div>
          <SaveButton />
        </div>
      )}

      {/* TOP Save Button */}
      {!hasUnsavedChanges && (
        <div className="flex justify-end">
          <SaveButton />
        </div>
      )}

      {/* ============================================= */}
      {/* SECTION 1: PROVIDER-SPECIFIC SETTINGS        */}
      {/* ============================================= */}
      
      {/* Section Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Provider-Einstellungen
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Aktuell: <span className="font-medium text-gray-700 dark:text-gray-300">{currentProvider.label}</span>
        </p>
      </div>
        
      {/* Provider Selection Grid */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Provider
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {providers.map(provider => (
            <button
              key={provider.id}
              onClick={() => handleProviderChange(provider.id)}
              className={`
                p-3 rounded-lg border theme-transition text-center relative
                ${llmProvider === provider.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }
              `}
            >
              {/* Saved API key indicator */}
              {savedProviders[provider.id] && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-success-500 rounded-full" title="API Key gespeichert" />
              )}
              <div className="font-medium text-gray-900 dark:text-white text-sm">
                {provider.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {provider.description}
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          Grüner Punkt = API Key gespeichert
        </p>
      </Card>

      {/* Clipboard Info Box */}
      {llmProvider === 'clipboard' && (
        <Card>
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
              Prompt Kopieren - Kein API-Aufruf
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Dieser Modus erzeugt den vollständigen Prompt, den du manuell in ChatGPT, Claude oder andere LLMs einfügen kannst.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc ml-5 space-y-1">
              <li>Kein API-Key erforderlich</li>
              <li>Keine Kosten</li>
              <li>Prompt wird zum Kopieren angezeigt</li>
            </ul>
          </div>
        </Card>
      )}

      {/* API Configuration - Provider Specific */}
      {llmProvider !== 'clipboard' && (
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          API Konfiguration
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
            ({currentProvider.label})
          </span>
        </h3>
        
        <div className="space-y-4">
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              API Key
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => handleApiKeyChange(e.target.value)}
                placeholder={llmProvider === 'custom' ? 'Optional für lokale Modelle' : 'API Key eingeben...'}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {/* Load saved key button */}
              {savedProviders[llmProvider] && !apiKey && (
                <button
                  onClick={handleLoadSavedKey}
                  className="px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800/40 theme-transition text-sm font-medium whitespace-nowrap"
                >
                  Gespeicherten Key laden
                </button>
              )}
            </div>
            
            {/* Saved key available hint */}
            {savedProviders[llmProvider] && !apiKey && (
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                💾 Ein gespeicherter API Key ist für {llmProvider.toUpperCase()} verfügbar
              </p>
            )}
            
            {/* Security Warning */}
            <div className="mt-2 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-3 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-base">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    <strong>Sicherheitshinweis:</strong> Der API-Key wird verschlüsselt in deinem Browser gespeichert. 
                    Dies ist nicht 100% sicher bei physischem Zugriff auf deinen Computer. 
                    Nutze API-Keys mit limitiertem Guthaben oder separate Test-Keys.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Save API Key Toggle */}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="saveApiKey"
                  checked={saveApiKey}
                  onChange={(e) => handleSaveApiKeyToggle(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <label htmlFor="saveApiKey" className="text-sm text-gray-600 dark:text-gray-400">
                  API Key für {llmProvider.toUpperCase()} beim Speichern merken
                </label>
              </div>
              {savedProviders[llmProvider] && (
                <span className="text-xs text-success-600 dark:text-success-400">
                  ✓ Key gespeichert
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Jeder Provider hat seinen eigenen gespeicherten API Key
            </p>
          </div>

          {/* Base URL (for custom/groq/openrouter) */}
          {(llmProvider === 'custom' || llmProvider === 'groq' || llmProvider === 'openrouter') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Base URL
              </label>
              <input
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.example.com/v1/chat/completions"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Model - Free text input only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Modell
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="z.B. gpt-4o, claude-3-5-sonnet-20241022, llama-3.3-70b-versatile"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {/* PDF Support Check Button - Only for OpenRouter */}
              {llmProvider === 'openrouter' && (
                <button
                  onClick={handleCheckPdfSupport}
                  disabled={checkingPdfSupport || !model}
                  className={`px-3 py-2 rounded-lg text-sm font-medium theme-transition whitespace-nowrap ${
                    checkingPdfSupport 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-wait'
                      : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/40'
                  }`}
                  title="Prüfe ob das Modell PDF-Dateien direkt verarbeiten kann"
                >
                  {checkingPdfSupport ? '...' : '📄 PDF-Check'}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Gib den genauen Modellnamen des Providers ein
              {llmProvider === 'openrouter' && ' • Für OpenRouter z.B. "openai/gpt-4o" oder "anthropic/claude-sonnet-4"'}
            </p>
            
            {/* PDF Support Check Result - Only for OpenRouter */}
            {llmProvider === 'openrouter' && pdfSupportResult && (
              <div className={`mt-3 p-3 rounded-lg border ${
                pdfSupportResult.hasSupport 
                  ? 'bg-success-50 dark:bg-success-900/20 border-success-300 dark:border-success-700'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
              }`}>
                <div className="flex items-start gap-2">
                  <span className="text-lg">
                    {pdfSupportResult.hasSupport ? '✅' : '📋'}
                  </span>
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${
                      pdfSupportResult.hasSupport 
                        ? 'text-success-800 dark:text-success-200'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {pdfSupportResult.hasSupport 
                        ? 'Native PDF-Unterstützung ✓' 
                        : 'Keine native PDF-Unterstützung'}
                    </p>
                    
                    {pdfSupportResult.inputModalities?.length > 0 && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Input-Modalitäten: {pdfSupportResult.inputModalities.join(', ')}
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {pdfSupportResult.hasSupport 
                        ? 'PDFs werden nativ vom Modell verarbeitet (beste Qualität)'
                        : 'PDFs werden mit pdf-text extrahiert (kostenlos)'}
                    </p>
                    
                    {pdfSupportResult.error && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                        ⚠️ {pdfSupportResult.error}
                      </p>
                    )}
                    
                    {pdfSupportResult.modelInfo && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                          Modell-Details anzeigen
                        </summary>
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          <p><span className="font-medium">Name:</span> {pdfSupportResult.modelInfo.name}</p>
                          <p><span className="font-medium">Context:</span> {pdfSupportResult.modelInfo.context_length?.toLocaleString()} Token</p>
                          {pdfSupportResult.modelInfo.pricing && (
                            <p><span className="font-medium">Preis:</span> ${(parseFloat(pdfSupportResult.modelInfo.pricing.prompt) * 1000000).toFixed(2)}/M Input</p>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      )}

      {/* ============================================= */}
      {/* SECTION 2: GLOBAL SETTINGS (ALL PROVIDERS)   */}
      {/* ============================================= */}
      
      {/* Section Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mt-8">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Allgemeine Einstellungen
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Gelten für alle Provider
        </p>
      </div>

      {/* Generation Parameters */}
      {llmProvider !== 'clipboard' && (
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Generierungsparameter
        </h3>
        
        <div className="space-y-4">
          {/* Temperature */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Temperature
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {temperature.toFixed(2)}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
            <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
              <span>Präzise</span>
              <span>Kreativ</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Tokens
            </label>
            <input
              type="number"
              value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value) || 8192)}
              min="1000"
              max="32000"
              step="1000"
              className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>
      )}

      {/* Output Settings */}
      {llmProvider !== 'clipboard' && (
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Output-Einstellungen
        </h3>
        
        <div className="space-y-4">
          {/* Structured Outputs */}
          {setUseStructuredOutputs && (
            <div>
              <Toggle
                checked={useStructuredOutputs}
                onChange={setUseStructuredOutputs}
                label="Structured Outputs verwenden"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-14">
                Erzwingt valides JSON im vorgegebenen Schema (nur bei unterstützten Providern)
              </p>
            </div>
          )}

          {/* JSON Repair */}
          {setEnableJsonRepair && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <Toggle
                checked={enableJsonRepair}
                onChange={setEnableJsonRepair}
                label="JSON-Reparatur aktivieren"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-14">
                Wenn das LLM kein valides JSON liefert, wird ein zweiter API-Call gemacht, um die Antwort zu reparieren
              </p>
            </div>
          )}

          {/* Fragen-Kontext for Reroll/Variation */}
          {setUseConversationHistory && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <Toggle
                checked={useConversationHistory}
                onChange={setUseConversationHistory}
                label="Fragen-Kontext mitsenden"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-14">
                Bei Reroll/Variation/Umformulieren alle generierten Fragen als Kontext mitsenden (vermeidet Duplikate, aber mehr Tokens)
              </p>
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Prompt Template Selection - positioned above System Prompt as it determines the prompt content */}
      {setSelectedTemplateId && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Fach-Vorlage
            </h3>
            <button
              onClick={handleCreateCustomTemplate}
              className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg theme-transition"
            >
              + Eigene Vorlage
            </button>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Die gewählte Fach-Vorlage bestimmt den System Prompt für das LLM.
          </p>
          
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {categoriesWithTemplates.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium theme-transition
                  ${selectedCategory === category.id
                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }
                `}
              >
                <span className="mr-1">{category.icon}</span>
                {category.name}
                {category.id === 'custom' && (
                  <span className="ml-1 text-xs bg-primary-500 text-white px-1 rounded">
                    {category.templates.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Templates for selected category */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {categoriesWithTemplates
              .find(c => c.id === selectedCategory)
              ?.templates.map(template => (
                <div
                  key={template.id}
                  className={`
                    relative p-3 rounded-lg border-2 text-left transition-all cursor-pointer
                    ${selectedTemplateId === template.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }
                  `}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="flex items-center gap-2">
                    <span>{template.icon}</span>
                    <span className="font-medium text-gray-900 dark:text-white text-sm flex-1">
                      {template.name}
                    </span>
                    {/* Edit/Delete buttons for custom templates */}
                    {template.isCustom && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditCustomTemplate(template)}
                          className="p-1 text-gray-400 hover:text-primary-500 theme-transition"
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteCustomTemplate(template.id)}
                          className="p-1 text-gray-400 hover:text-red-500 theme-transition"
                          title="Löschen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {/* Fork button for system templates (if logged in) */}
                    {!template.isCustom && userId && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleForkTemplate(template.id)}
                          className="p-1 text-gray-400 hover:text-primary-500 theme-transition"
                          title="Als eigene Vorlage kopieren"
                        >
                          📋
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {template.description}
                  </p>
                  {/* Forked indicator */}
                  {template.isForked && (
                    <span className="text-xs text-primary-500 mt-1">
                      (Angepasst)
                    </span>
                  )}
                </div>
              ))}
          </div>

          {/* Example Tags for current template */}
          {currentExampleTags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Beispiel-Tags für {currentTemplate?.name}:
              </p>
              <div className="flex flex-wrap gap-1">
                {currentExampleTags.slice(0, 6).map(tag => (
                  <span 
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Custom Template Editor Modal */}
      {showCustomTemplateEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
                </h3>
                <button
                  onClick={() => setShowCustomTemplateEditor(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Name and Icon */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Icon
                    </label>
                    <input
                      type="text"
                      value={customTemplateForm.icon}
                      onChange={(e) => setCustomTemplateForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl"
                      placeholder="📝"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={customTemplateForm.name}
                      onChange={(e) => setCustomTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="z.B. Wirtschaft & BWL"
                    />
                  </div>
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beschreibung
                  </label>
                  <input
                    type="text"
                    value={customTemplateForm.description}
                    onChange={(e) => setCustomTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Kurze Beschreibung des Fachbereichs"
                  />
                </div>
                
                {/* System Prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    System Prompt
                  </label>
                  <textarea
                    value={customTemplateForm.systemPrompt}
                    onChange={(e) => setCustomTemplateForm(prev => ({ ...prev, systemPrompt: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    rows={12}
                    placeholder="Instruktionen für das LLM..."
                  />
                </div>
                
                {/* Example Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beispiel-Tags (kommasepariert)
                  </label>
                  <input
                    type="text"
                    value={customTemplateForm.exampleTags}
                    onChange={(e) => setCustomTemplateForm(prev => ({ ...prev, exampleTags: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="z.B. BWL, VWL, Marketing, Finanzen"
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowCustomTemplateEditor(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveCustomTemplate}
                  disabled={!customTemplateForm.name || !customTemplateForm.systemPrompt}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingTemplate ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Prompt */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            System Prompt
          </h3>
          <button
            onClick={() => setShowSystemPrompt(!showSystemPrompt)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            {showSystemPrompt ? 'Ausblenden' : 'Anzeigen'}
          </button>
        </div>
        
        {showSystemPrompt && (
          <div className="space-y-3">
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={() => setSystemPrompt(getDefaultSystemPrompt())}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Auf Standard zurücksetzen
            </button>
          </div>
        )}
        
        {!showSystemPrompt && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {systemPrompt.length} Zeichen konfiguriert
          </p>
        )}
      </Card>

      {/* Actions - Bottom */}
      <Card padding="sm">
        <div className="flex gap-3 items-center">
          <SaveButton className="flex-1" />
          <button
            onClick={onResetSettings}
            className="py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 theme-transition"
          >
            Zurücksetzen
          </button>
        </div>
        {hasUnsavedChanges && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 text-center">
            Vergiss nicht zu speichern!
          </p>
        )}
      </Card>
    </div>
  );
}

LLMSettingsTab2.propTypes = {
  llmProvider: PropTypes.string.isRequired,
  setLlmProvider: PropTypes.func.isRequired,
  apiKey: PropTypes.string,
  setApiKey: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  setBaseUrl: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  setModel: PropTypes.func.isRequired,
  temperature: PropTypes.number.isRequired,
  setTemperature: PropTypes.func.isRequired,
  maxTokens: PropTypes.number.isRequired,
  setMaxTokens: PropTypes.func.isRequired,
  useStructuredOutputs: PropTypes.bool,
  setUseStructuredOutputs: PropTypes.func,
  enableJsonRepair: PropTypes.bool,
  setEnableJsonRepair: PropTypes.func,
  useConversationHistory: PropTypes.bool,
  setUseConversationHistory: PropTypes.func,
  systemPrompt: PropTypes.string.isRequired,
  setSystemPrompt: PropTypes.func.isRequired,
  getDefaultSystemPrompt: PropTypes.func.isRequired,
  onSaveSettings: PropTypes.func.isRequired,
  onResetSettings: PropTypes.func.isRequired,
  onUnsavedChangesChange: PropTypes.func, // Callback when unsaved changes state changes
  selectedTemplateId: PropTypes.string,
  setSelectedTemplateId: PropTypes.func,
  // User ID for Firebase template storage
  userId: PropTypes.string
};

export default LLMSettingsTab2;
